import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { Button } from "@/components/ui/button";
import {
  socialQueueCancel,
  socialQueueOverview,
  socialQueueRetry,
} from "@/lib/social-queue.functions";

export const Route = createFileRoute("/admin/social/queue")({
  component: QueuePage,
  head: () => ({
    meta: [
      { title: "Publish queue · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function QueuePage() {
  const qc = useQueryClient();
  const overview = useServerFn(socialQueueOverview);
  const retry = useServerFn(socialQueueRetry);
  const cancel = useServerFn(socialQueueCancel);

  const q = useQuery({ queryKey: ["social-queue"], queryFn: () => overview() });
  const data: any = q.data ?? {};

  async function onRetry(id: string) {
    try {
      await retry({ data: { id } });
      toast.success("Requeued");
      await qc.invalidateQueries({ queryKey: ["social-queue"] });
    } catch (err: any) { toast.error(err?.message ?? "Retry failed"); }
  }
  async function onCancel(id: string) {
    if (!confirm("Cancel this scheduled post (returns it to drafts)?")) return;
    try {
      await cancel({ data: { id } });
      toast.success("Cancelled");
      await qc.invalidateQueries({ queryKey: ["social-queue"] });
    } catch (err: any) { toast.error(err?.message ?? "Cancel failed"); }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="mb-2 text-2xl font-bold">Publish queue</h1>
      <p className="mb-4 text-sm text-muted-foreground">
        Scheduled and failed posts, cron heartbeat, and retry actions. Retry uses exponential backoff.
      </p>
      <SocialSubNav />

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            {[
              ["Scheduled total", data.counts?.scheduledTotal ?? 0],
              ["Next 24h", data.counts?.next24h ?? 0],
              ["Next 7d", data.counts?.next7d ?? 0],
              ["Failed", data.counts?.failedTotal ?? 0],
            ].map(([label, val]) => (
              <div key={label as string} className="rounded-lg border border-border bg-card p-3">
                <div className="text-xs text-muted-foreground">{label as string}</div>
                <div className="text-2xl font-bold">{val as number}</div>
              </div>
            ))}
          </div>

          <section className="mb-6 rounded-lg border border-border bg-card p-4">
            <h2 className="mb-2 text-lg font-semibold">pg_cron jobs (social)</h2>
            {(data.cronJobs ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No social cron jobs visible (RLS may hide the cron.job table from this role — check Diagnostics).</p>
            ) : (
              <ul className="text-xs">
                {(data.cronJobs ?? []).map((j: any) => (
                  <li key={j.jobid} className="border-b border-border py-1">
                    <span className="font-mono">{j.jobname}</span> — {j.schedule} {j.active ? "" : "(inactive)"}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mb-8">
            <h2 className="mb-2 text-lg font-semibold">Scheduled</h2>
            <div className="max-h-96 overflow-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted text-left">
                  <tr>
                    <th className="p-2">When</th>
                    <th className="p-2">Platforms</th>
                    <th className="p-2">Body</th>
                    <th className="p-2">Compliance</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.scheduled ?? []).map((p: any) => (
                    <tr key={p.id} className="border-t border-border align-top">
                      <td className="p-2">{p.scheduled_at ? new Date(p.scheduled_at).toLocaleString() : "—"}</td>
                      <td className="p-2">{(p.platforms ?? []).join(", ")}</td>
                      <td className="p-2 max-w-[380px] truncate" title={p.body}>{p.body}</td>
                      <td className="p-2">{p.compliance_status ?? "pending"}</td>
                      <td className="p-2 text-right"><Button size="sm" variant="outline" onClick={() => onCancel(p.id)}>Cancel</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="mb-2 text-lg font-semibold">Failed</h2>
            <div className="max-h-96 overflow-auto rounded border border-border">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-muted text-left">
                  <tr>
                    <th className="p-2">Last error</th>
                    <th className="p-2">Retries</th>
                    <th className="p-2">Platforms</th>
                    <th className="p-2">Body</th>
                    <th className="p-2">Message</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.failed ?? []).map((p: any) => (
                    <tr key={p.id} className="border-t border-border align-top">
                      <td className="p-2">{p.last_error_at ? new Date(p.last_error_at).toLocaleString() : "—"}</td>
                      <td className="p-2">{p.retry_count ?? 0}</td>
                      <td className="p-2">{(p.platforms ?? []).join(", ")}</td>
                      <td className="p-2 max-w-[280px] truncate" title={p.body}>{p.body}</td>
                      <td className="p-2 max-w-[240px] truncate" title={p.error_message}>{p.error_message ?? "—"}</td>
                      <td className="p-2 text-right"><Button size="sm" onClick={() => onRetry(p.id)}>Retry</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
